import { message } from 'antd'

// helpers
const stripHtmlTags = (value = '') =>
  value
    .replace(/<[^>]+>/g, ' ') // remove tags
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim()

const isHtml = value => /<\/?[a-z][\s\S]*>/i.test(value)

const looksLikeTranslationKey = value =>
  !isHtml(value) && // is not html
  !/\s/.test(value) && // no spaces
  /[a-zA-Z]/.test(value) && // has letters
  value.includes('.') // like "fcm.errors.defaultError"

/**
 * Handles error responses and returns translated error messages
 * @param {Object} error - The error object from axios
 * @param {Object} intl - The react-intl object
 * @param {Object} options - Additional options
 * @param {boolean} options.showNotification - Whether to show antd notification (default: false)
 * @param {string} options.fallbackMessage - Custom fallback message key
 * @returns {string} - The translated error message
 */
export const handleError = (error, intl, options = {}) => {
  const { showNotification = false, fallbackMessage } = options

  let translatedMessage = ''
  let errorData = null

  // Extract error data from different possible response structures
  if (error?.response?.data) {
    errorData = error.response.data
  } else if (error?.data) {
    errorData = error.data
  } else if (error?.translation_id) {
    errorData = error
  }

  // Handle structured FCM error response
  if (errorData?.translation_id && errorData?.fields) {
    try {
      translatedMessage = intl.formatMessage(
        { id: errorData.translation_id },
        errorData.fields
      )
    } catch (formatError) {
      translatedMessage = intl.formatMessage(
        { id: 'fcm.errors.defaultError' },
        errorData.fields
      )
    }
  }
  // Handle legacy error messages (message_error field)
  else if (errorData?.message_error) {
    translatedMessage = intl.formatMessage(
      { id: errorData.message_error },
      {},
      errorData.message_error
    )
  }
  // Handle simple string errors (incluindo HTML)
  else if (typeof errorData === 'string') {
    const plainText = isHtml(errorData) ? stripHtmlTags(errorData) : errorData

    if (looksLikeTranslationKey(plainText)) {
      // looks like a translation key
      translatedMessage = intl.formatMessage({ id: plainText }, {}, plainText)
    } else {
      // is a raw message (ex: 504 Gateway, server error, etc)
      translatedMessage = plainText
    }
  }
  // Default fallback
  else {
    const fallbackKey = fallbackMessage || 'fcm.errors.defaultError'
    translatedMessage = intl.formatMessage({ id: fallbackKey })
  }

  if (showNotification) {
    message.error(translatedMessage, 2.5)
  }

  return translatedMessage
}

/**
 * Extracts error details for debugging purposes
 * @param {Object} error - The error object
 * @returns {Object} - Error details
 */
export const getErrorDetails = error => {
  const errorData = error?.response?.data || error?.data || error

  return {
    translationId: errorData?.translation_id,
    fields: errorData?.fields,
    statusCode: errorData?.status_code || error?.response?.status,
    details: errorData?.details,
    originalError: error,
  }
}

/**
 * Checks if an error is a specific error type
 * @param {Object} error - The error object
 * @param {string} translationId - The translation ID to check for
 * @returns {boolean} - Whether the error matches the translation ID
 */
export const isErrorType = (error, translationId) => {
  const errorData = error?.response?.data || error?.data || error
  return errorData?.translation_id === translationId
}

/**
 * Gets the error fields for additional context
 * @param {Object} error - The error object
 * @returns {Object} - The error fields
 */
export const getErrorFields = error => {
  const errorData = error?.response?.data || error?.data || error
  return errorData?.fields || {}
}
